import { Injectable } from '@angular/core';
import { UserCredential } from './webauthn.service';

@Injectable({
  providedIn: 'root',
})
export default class MemoryAdapter {
  private key!: CryptoKey;
  constructor() {}

  public async createKey(): Promise<void> {
    this.key = await this.getKey('fwt8zpe!npq5ygw6BZK');
  }

  public getUser(id: string): UserCredential | null {
    const item = localStorage.getItem(id);

    if (!item) {
      return null;
    }

    return JSON.parse(item) as UserCredential;
  }

  public putUser(id: string, user: UserCredential): void {
    localStorage.setItem(id, JSON.stringify(user));
  }

  public getValue(key: string): string | null {
    const item = localStorage.getItem(key);

    if (!item) {
      return null;
    }

    return item;
  }

  public putValue(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  public async getCred(): Promise<{ username: string; password: string } | null> {
    const user = localStorage.getItem('user') ?? '';
    const ivStr = localStorage.getItem(`iv-${user}`) ?? '';
    const pwdStr = localStorage.getItem(`pwd-${user}`) ?? '';

    let decryptedPwd = '';
    if (ivStr) {
      const iv = new Uint8Array(ivStr.split(',').map((x) => +x));
      const pwd = this.strToArrayBuffer(pwdStr);

      decryptedPwd = await this.decrypt(pwd, iv);
    }

    if (!decryptedPwd) {
      return null;
    }

    return { username: user, password: decryptedPwd };
  }

  public async putCred(username: string, password: string): Promise<void> {
    localStorage.setItem('user', username);
    const ivStr = localStorage.getItem(`iv-${username}`) ?? '';
    let iv;
    if (!ivStr) {
      iv = crypto.getRandomValues(new Uint8Array(12));
      localStorage.setItem(`iv-${username}`, iv.toString());
    } else {
      iv = new Uint8Array(ivStr.split(',').map((x) => +x));
    }
    const encryptedPwd = await this.encrypt(password, iv);
    localStorage.setItem(`pwd-${username}`, new Uint8Array(encryptedPwd).toString());
  }

  public async delete(id: string): Promise<boolean> {
    if (localStorage.getItem(id)) {
      localStorage.removeItem(id);
      return true;
    }

    return false;
  }

  private strToArrayBuffer(value: string): ArrayBuffer {
    const valueArray = value.split(',');
    const toDecrypt = new ArrayBuffer(valueArray.length);
    const dataView = new DataView(toDecrypt);

    for (let i = 0; i < valueArray.length; i++) {
      dataView.setInt8(i, +valueArray[i]);
    }

    return toDecrypt;
  }

  private async encrypt(value: string, iv: BufferSource): Promise<ArrayBuffer> {
    const encrypted = await crypto.subtle.encrypt(
      {
        iv,
        name: 'AES-GCM',
      },
      this.key,
      new TextEncoder().encode(value),
    );
    return encrypted;
  }

  private async decrypt(value: ArrayBuffer, iv: BufferSource): Promise<string> {
    const decrypted = await crypto.subtle.decrypt(
      {
        iv,
        name: 'AES-GCM',
      },
      this.key,
      value,
    );
    return new TextDecoder().decode(decrypted);
  }

  private async getKey(password: string): Promise<CryptoKey> {
    const encryptKey = await this.sha256(password);

    return crypto.subtle.importKey('raw', encryptKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  }

  private async sha256(value: string): Promise<ArrayBuffer> {
    return crypto.subtle.digest('sha-256', new TextEncoder().encode(value));
  }
}
